import { useRef } from "react";
import { Plus, Trash2 } from "lucide-react";
import NumberInput from "./NumberInput";

const clampPercent = (value) => Math.max(0, Math.min(100, Number(value) || 0));

export const normalizeGradientStops = (stops, fallback = ["#4f46e5", "#a855f7"]) => {
  const safeStops = Array.isArray(stops) ? stops : [];
  const normalized = safeStops
    .map((stop, index) => ({
      id: stop?.id || `stop-${index}-${Date.now()}`,
      position: clampPercent(stop?.position ?? index * 100),
      color: stop?.color || fallback[index] || fallback[fallback.length - 1] || "#4f46e5",
      opacity: clampPercent(stop?.opacity ?? 100),
    }))
    .sort((a, b) => a.position - b.position);

  if (normalized.length >= 2) {
    return normalized;
  }

  return [
    {
      id: "stop-0",
      position: 0,
      color: fallback[0] || "#4f46e5",
      opacity: 100,
    },
    {
      id: "stop-1",
      position: 100,
      color: fallback[1] || "#a855f7",
      opacity: 100,
    },
  ];
};

export const buildGradientBackground = ({
  mode,
  solidColor,
  angle,
  stops,
  fallback = ["#4f46e5", "#a855f7"],
}) => {
  if (mode !== "gradient") {
    return solidColor;
  }

  const safeStops = normalizeGradientStops(stops, fallback);
  const gradientStops = safeStops.map((stop) => {
    const normalizedOpacity = Math.max(0, Math.min(100, Number(stop.opacity ?? 100))) / 100;
    const color = stop.color || fallback[0] || "#4f46e5";
    if (color === "transparent" || color === "none") {
      return `rgba(255,255,255,0) ${stop.position}%`;
    }
    const hex = color.replace("#", "");
    const value = hex.length === 3
      ? hex
          .split("")
          .map((char) => char + char)
          .join("")
      : hex;
    const intValue = Number.parseInt(value, 16);
    const red = (intValue >> 16) & 255;
    const green = (intValue >> 8) & 255;
    const blue = intValue & 255;
    return `rgba(${red}, ${green}, ${blue}, ${normalizedOpacity}) ${stop.position}%`;
  });

  return `linear-gradient(${Number(angle ?? 135)}deg, ${gradientStops.join(", ")})`;
};

export default function GradientEditor({
  mode,
  onModeChange,
  angle,
  onAngleChange,
  stops,
  onStopsChange,
  fallback = ["#4f46e5", "#a855f7"],
}) {
  const gradientTrackRef = useRef(null);
  const safeStops = normalizeGradientStops(stops, fallback);
  const previewBackground = buildGradientBackground({
    mode: "gradient",
    angle,
    stops: safeStops,
    fallback,
  });

  const updateStop = (id, patch) => {
    onStopsChange(
      safeStops
        .map((stop) => {
          if (stop.id !== id) {
            return stop;
          }
          const nextStop = { ...stop, ...patch };
          if (Object.prototype.hasOwnProperty.call(patch, "position")) {
            nextStop.position = clampPercent(patch.position);
          }
          if (Object.prototype.hasOwnProperty.call(patch, "opacity")) {
            nextStop.opacity = clampPercent(patch.opacity);
          }
          return nextStop;
        })
        .sort((a, b) => a.position - b.position),
    );
  };

  const removeStop = (id) => {
    if (safeStops.length <= 2) {
      return;
    }
    onStopsChange(safeStops.filter((stop) => stop.id !== id));
  };

  const addStop = () => {
    const midPosition =
      safeStops.length === 0
        ? 50
        : Math.round(
            safeStops.reduce((sum, stop) => sum + Number(stop.position || 0), 0) /
              safeStops.length,
          );
    onStopsChange(
      [...safeStops, { id: `stop-${Date.now()}`, position: midPosition, color: "#7c3aed", opacity: 100 }].sort(
        (a, b) => a.position - b.position,
      ),
    );
  };

  const getStopHandleLeft = (position) => {
    const safePosition = clampPercent(position);
    return `calc(12px + ${safePosition}% - ${(safePosition / 100) * 24}px)`;
  };

  const handleStopPointerDown = (event, id) => {
    const track = gradientTrackRef.current;
    if (!track) {
      return;
    }

    event.preventDefault();

    const updateFromClientX = (clientX) => {
      const rect = track.getBoundingClientRect();
      const position = clampPercent(((clientX - rect.left) / rect.width) * 100);
      updateStop(id, { position: Math.round(position) });
    };

    updateFromClientX(event.clientX);

    const handlePointerMove = (moveEvent) => {
      updateFromClientX(moveEvent.clientX);
    };

    const handlePointerUp = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  const directionPresets = [
    { label: "→", angle: 90 },
    { label: "←", angle: 270 },
    { label: "↓", angle: 180 },
    { label: "↑", angle: 0 },
    { label: "↘", angle: 135 },
    { label: "↗", angle: 45 },
    { label: "↙", angle: 225 },
    { label: "↖", angle: 315 },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
          Fill
        </div>
        <div className="flex rounded-xl bg-gray-100 p-1 shrink-0">
          {[
            { value: "solid", label: "Solid" },
            { value: "gradient", label: "Gradient" },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onModeChange(option.value)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                mode === option.value
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {mode === "gradient" && (
        <>
          <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-4 overflow-hidden">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <select
                  value="linear"
                  disabled
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 outline-none appearance-none"
                >
                  <option value="linear">Linear</option>
                </select>
              </div>
              <button
                type="button"
                onClick={() =>
                  onStopsChange([...safeStops].reverse().map((stop) => ({
                    ...stop,
                    position: 100 - stop.position,
                  })))
                }
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50"
              >
                ↔
              </button>
            </div>

            <div className="space-y-3">
              <div className="relative px-3 pt-6">
                <div
                  ref={gradientTrackRef}
                  className="h-16 rounded-xl border border-gray-200 shadow-inner"
                  style={{ background: previewBackground }}
                ></div>
                {safeStops.map((stop) => (
                  <div
                    key={stop.id}
                    className="absolute top-0 -translate-x-1/2 cursor-ew-resize touch-none"
                    style={{ left: getStopHandleLeft(stop.position) }}
                    onPointerDown={(event) => handleStopPointerDown(event, stop.id)}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <div className="h-8 w-8 rounded-lg border border-gray-300 bg-white p-1 shadow-md">
                        <div
                          className="h-full w-full rounded-md border border-gray-200"
                          style={{ background: stop.color }}
                        ></div>
                      </div>
                      <div className="-mt-2 h-2.5 w-2.5 rotate-45 rounded-[2px] border-b border-r border-gray-300 bg-white"></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Stops</div>
                <button
                  type="button"
                  onClick={addStop}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-600 transition-all hover:border-gray-300 hover:bg-gray-50"
                >
                  <Plus size={14} />
                  Add
                </button>
              </div>

              <div className="space-y-2">
                {safeStops.map((stop) => (
                  <div
                    key={stop.id}
                    className="grid grid-cols-[78px_minmax(0,1fr)_78px_32px] gap-1.5 rounded-xl border border-gray-200 bg-gray-50 p-1.5"
                  >
                    <div className="flex min-w-0 items-center rounded-lg border border-gray-200 bg-white focus-within:border-indigo-300">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={stop.position}
                        onChange={(e) =>
                          updateStop(stop.id, { position: e.target.value })
                        }
                        className="min-w-0 flex-1 bg-transparent pl-2 pr-1 py-2 text-sm text-gray-700 outline-none"
                      />
                      <span className="pr-2 text-xs font-semibold text-gray-400">%</span>
                    </div>
                    <div className="flex min-w-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-1.5">
                      <label
                        className="relative h-8 w-8 shrink-0 overflow-hidden rounded-lg border border-gray-200"
                        style={{ background: stop.color }}
                      >
                        <input
                          type="color"
                          value={stop.color === "transparent" || stop.color === "none" ? "#ffffff" : stop.color || "#000000"}
                          onChange={(e) =>
                            updateStop(stop.id, { color: e.target.value })
                          }
                          className="absolute -inset-1 h-[150%] w-[150%] cursor-pointer opacity-0"
                        />
                      </label>
                      <input
                        type="text"
                        value={stop.color}
                        onChange={(e) =>
                          updateStop(stop.id, { color: e.target.value })
                        }
                        className="min-w-0 flex-1 bg-transparent py-2 text-sm text-gray-700 outline-none"
                      />
                    </div>
                    <div className="flex min-w-0 items-center rounded-lg border border-gray-200 bg-white focus-within:border-indigo-300">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={stop.opacity}
                        onChange={(e) =>
                          updateStop(stop.id, { opacity: e.target.value })
                        }
                        className="min-w-0 flex-1 bg-transparent pl-2 pr-1 py-2 text-sm text-gray-700 outline-none"
                      />
                      <span className="pr-2 text-xs font-semibold text-gray-400">%</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeStop(stop.id)}
                      disabled={safeStops.length <= 2}
                      className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-400 transition-all hover:border-red-100 hover:bg-red-50 hover:text-red-500 disabled:opacity-40"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-3">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  Direction
                </div>
              </div>
              <div
                className="relative h-20 w-20 shrink-0 rounded-xl border border-gray-200 bg-[linear-gradient(180deg,#ffffff,#f3f4f6)]"
              >
                <div
                  className="absolute left-1/2 top-1/2 h-[2px] w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-500"
                  style={{ transform: `translate(-50%, -50%) rotate(${Number(angle ?? 135)}deg)` }}
                >
                  <span className="absolute -right-1.5 -top-[5px] h-3.5 w-3.5 rounded-full bg-white border border-indigo-400 shadow-sm"></span>
                  <span className="absolute -left-1.5 -top-[5px] h-3.5 w-3.5 rounded-full bg-white border border-indigo-400 shadow-sm"></span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {directionPresets.map((preset) => (
                <button
                  key={preset.angle}
                  type="button"
                  onClick={() => onAngleChange(preset.angle)}
                  className={`rounded-lg border px-2 py-1.5 text-sm font-semibold transition-all ${
                    Number(angle ?? 135) === preset.angle
                      ? "border-indigo-200 bg-indigo-50 text-indigo-600"
                      : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <NumberInput
              label="Angle"
              value={Number(angle ?? 135)}
              min={0}
              max={360}
              onChange={onAngleChange}
            />
          </div>
        </>
      )}
    </div>
  );
}
